import React, { useEffect, useState } from 'react';
import {
  Alert, Box, TextField, Button, Typography,
  MenuItem, FormControl, InputLabel, Select, DialogActions, DialogContent
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { Dialog, DialogTitle } from '../Common/ModalDialog';
import {
  API_GET_FEE_BOOK_STUDENTS,
  API_ADD_FEE,
  API_PREVIEW_FEE_BOOK_TUITION,
  API_UPDATE_FEE,
  API_GET_ACADEMIC_SESSIONS,
} from "../Config/ApiUrlConstant";
import { useAuth } from "../AuthContext";
import { ClassListArray } from "../Common/Component/CommonComponents";

const amountFields = ['tuition_charge', 'magic_charge', 'fee_pending', 'discount'];
const normalizeAmounts = (data) => Object.fromEntries(Object.entries(data).map(([key, value]) => [key, amountFields.includes(key) && (value === '' || value === null || value === undefined) ? 0 : value]));

const AddStudentFeeForm = ({ onSuccess, initialData = null, mode = "add" }) => {
  const isEdit = mode === "edit";
  const { logout } = useAuth();

  const [formData, setFormData] = useState({
    id: '',
    class_no: '',
    student_id: '',
    academic_session_id: '',
    fee_months: [],
    tuition_charge: 0,
    magic_charge: 0,
    fee_pending: 0,
    description: '',
    discount: 0,
    created_at: new Date().toISOString().split('T')[0],
  });

  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [rates, setRates] = useState([]);
  const [rateError, setRateError] = useState('');
  const [ratesLoading, setRatesLoading] = useState(false);
  const [rateReload, setRateReload] = useState(0);
  const [academicSessions, setAcademicSessions] = useState([]);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const abort = new AbortController(); setStudents([]);
    if (!formData.academic_session_id || !formData.class_no) return;
    fetch(API_GET_FEE_BOOK_STUDENTS, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ academic_session_id: formData.academic_session_id, class_name: formData.class_no }), signal: abort.signal })
      .then(async (response) => { const payload = await response.json(); if (!response.ok || !payload.status) throw new Error(payload.message); setStudents(payload.data || []); })
      .catch((error) => { if (error.name !== 'AbortError') setFeedback(error.message || 'Unable to load students.'); });
    return () => abort.abort();
  }, [formData.academic_session_id, formData.class_no]);

  // Set initialData for edit
  useEffect(() => {
    if (initialData) {
      let months = [];
      if (initialData.fee_month) {
        months = Array.isArray(initialData.fee_month)
          ? initialData.fee_month.map(m => m.substring(0, 7)) // keep only YYYY-MM
          : initialData.fee_month.split(',').map(m => m.substring(0, 7));
      }
      setFormData({
        id: initialData.id || '',
        class_no: initialData.class || '',
        student_id: initialData.student_id || '',
        academic_session_id: initialData.academic_session_id || '',
        fee_months: months,
        tuition_charge: initialData.tuition_charge || 0,
        magic_charge: initialData.magic_charge || 0,
        fee_pending: initialData.fee_pending || 0,
        description: initialData.description || '',
        discount: initialData.discount || 0,
        created_at: initialData.created_at?.split("T")[0] || new Date().toISOString().split('T')[0],
      });
    }
  }, [initialData]);

  useEffect(() => {
    fetch(API_GET_ACADEMIC_SESSIONS, { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then((response) => response.json()).then((payload) => setAcademicSessions(payload.data || [])).catch(() => setFeedback('Unable to load sessions.'));
  }, []);

  useEffect(() => { setFilteredStudents(students.filter((student) => String(student.class) === String(formData.class_no))); }, [formData.class_no, students]);

  const monthKey = formData.fee_months.join(',');
  useEffect(() => {
    if (isEdit) return;
    const abort = new AbortController();
    setRates([]); setRateError(''); setRatesLoading(false);
    if (!formData.student_id || !formData.academic_session_id || !monthKey) return;
    setRatesLoading(true);
    fetch(API_PREVIEW_FEE_BOOK_TUITION, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ student_id: formData.student_id, academic_session_id: formData.academic_session_id, fee_months: monthKey.split(',') }), signal: abort.signal })
      .then(async (response) => { const payload = await response.json(); if (!response.ok || !payload.status) throw new Error(payload.message); setRates(payload.data); })
      .catch((error) => { if (error.name !== 'AbortError') setRateError(error.message || 'Unable to load rates.'); })
      .finally(() => { if (!abort.signal.aborted) setRatesLoading(false); });
    return () => abort.abort();
  }, [isEdit, formData.student_id, formData.academic_session_id, monthKey, rateReload]);
  const selectedSession = academicSessions.find((session) => String(session.id) === String(formData.academic_session_id));
  const availableMonths = [];
  if (selectedSession) {
    const cursor = new Date(selectedSession.start_date.slice(0, 7) + '-01T00:00:00Z');
    while (cursor.toISOString().slice(0, 10) <= selectedSession.end_date.slice(0, 10) && availableMonths.length < 120) {
      if (cursor.toISOString().slice(0, 10) >= selectedSession.start_date.slice(0, 10)) availableMonths.push(cursor.toISOString().slice(0, 7));
      cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value, ...(!isEdit && name === 'academic_session_id' ? { fee_months: [], student_id: '' } : {}), ...(!isEdit && name === 'class_no' ? { student_id: '' } : {}) }));
  };

  const handleMonthChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      fee_months: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const invalidField = amountFields.find((field) => {
      const value = formData[field];
      return value !== '' && value !== null && value !== undefined && (!Number.isFinite(Number(value)) || Number(value) < 0);
    });
    if (invalidField) {
      setFeedback('All fee amounts must be valid non-negative numbers.');
      return;
    }
    try {
      const token = localStorage.getItem('school_token');
      const apiUrl = isEdit ? API_UPDATE_FEE : API_ADD_FEE;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...normalizeAmounts(formData), ...(!isEdit ? { fee_book_versions: Object.fromEntries(rates.map((rate) => [rate.month, rate.version_id])) } : {}) }),
      });

      const responseData = await response.json();
      if (response.status === 401 || response.status === 403) { logout(); return; }

      if (responseData.status) {
        if (onSuccess) onSuccess();
      } else {
        setFeedback(responseData.message || "Something went wrong while saving the fee.");
      }
    } catch (err) {
      console.error("Error submitting fee:", err);
    }
  };

  return (
    <Box>
      {/* <Typography variant="h6" gutterBottom>
        {isEdit ? "Update Fee" : "Add Monthly Fee"}
      </Typography> */}
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Class */}
          <Grid item xs={12} sm={6} style={{ width: '120px' }}>
            <FormControl fullWidth required>
              <InputLabel>Class</InputLabel>
              <Select
                name="class_no"
                value={formData.class_no}
                onChange={handleChange}
                label="Class"
              >
                <MenuItem value="">Select Class</MenuItem>
                {ClassListArray.map(cls => (
                  <MenuItem key={cls.value} value={cls.value}>{cls.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Student */}
          <Grid item xs={12} sm={6} style={{ width: '290px' }}>
            <FormControl fullWidth required>
              <InputLabel>Student</InputLabel>
              <Select
                name="student_id"
                value={formData.student_id}
                onChange={handleChange}
                disabled={!formData.class_no || !formData.academic_session_id}
              >
                <MenuItem value="">Select Student</MenuItem>
                {filteredStudents.map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.student_name} ({s.student_father_name})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} style={{ width: '200px' }}>
            <FormControl fullWidth required>
              <InputLabel>Academic Session</InputLabel>
              <Select name="academic_session_id" value={formData.academic_session_id} onChange={handleChange} label="Academic Session">
                <MenuItem value="">Select session</MenuItem>
                {academicSessions.map((session) => <MenuItem key={session.id} value={session.id}>{session.session_code}{session.is_active ? ' (Current)' : ''}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          {/* Months */}
          <Grid item xs={12} sm={6} style={{ width: '200px' }}>
            <FormControl fullWidth required>
              <InputLabel>Fee Months</InputLabel>
              <Select
                multiple
                name="fee_months"
                value={Array.isArray(formData.fee_months) ? formData.fee_months : []}
                onChange={handleMonthChange}
                renderValue={(selected) => selected.join(', ')}
              >
                {availableMonths.map((month) => <MenuItem key={month} value={month}>{month}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          {!isEdit && <Grid item xs={12}>
            <Alert severity={rateError ? 'error' : 'info'}>{rateError || (ratesLoading ? 'Loading dated tuition rates…' : rates.length ? rates.map((rate) => rate.month + ': ' + Number(rate.amount).toFixed(2)).join(' · ') : 'Select a student, session, and months to preview tuition rates.')}<br />Rates apply on the first day of each selected month.</Alert>
            <Button onClick={() => setRateReload((old) => old + 1)}>Refresh rates</Button>
          </Grid>}
          {/* Charges */}
          <Grid item xs={12} sm={6}>
            <TextField
              name="tuition_charge"
              label="Tuition Charge"
              type="number"
              fullWidth
              value={isEdit ? formData.tuition_charge : rates[0]?.amount ?? ''}
              disabled={!isEdit}
              helperText={!isEdit ? 'Dated rates shown above are applied separately to each month.' : ''}
              onChange={handleChange}
              required
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="magic_charge"
              label="Magic Charge"
              type="number"
              fullWidth
              value={formData.magic_charge}
              onChange={handleChange}
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="fee_pending"
              label="Fee Pending"
              type="number"
              fullWidth
              value={formData.fee_pending}
              onChange={handleChange}
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="discount"
              label="Discount"
              type="number"
              fullWidth
              value={formData.discount}
              onChange={handleChange}
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="description"
              label="Description"
              fullWidth
              value={formData.description}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="created_at"
              label="Created Date"
              type="date"
              fullWidth
              value={formData.created_at}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" type="submit" fullWidth disabled={!isEdit && (ratesLoading || Boolean(rateError) || !rates.length || rates.length !== formData.fee_months.length)}>
              {isEdit ? "Update Fee" : "Add Fee"}
            </Button>
          </Grid>
        </Grid>
      </form>
      <Dialog open={Boolean(feedback)} onClose={() => setFeedback(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Unable to save fee</DialogTitle>
        <DialogContent><Typography>{feedback}</Typography></DialogContent>
        <DialogActions><Button variant="contained" onClick={() => setFeedback(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddStudentFeeForm;
